import { formatDate } from '@utils/datetime';
import { getWordCount } from '@utils/journals/franchises';
import { readingTimeCalculator } from '@utils/readingTimeCalculator';
import { fetchStories } from '@utils/storyblok/stories';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';
import { StoryData } from 'storyblok-js-client';

import {
  FranchiseStoryblok,
  InsightIdStoryblok,
} from '../../types/generated-storyblok';
import { useAnalyticsEmitter } from '../../utils/analytics/emitter';
import { ArticleItem, ArticleItemSkeleton } from './ArticleItemSkeleton';

export const ContentInsight = ({
  blok,
  variant,
}: {
  blok: InsightIdStoryblok;
  variant: 'card' | 'list';
}) => {
  const { t } = useTranslation('common');
  const [emitter] = useAnalyticsEmitter();

  const [insight, setInsight] = useState<FranchiseStoryblok>();
  const [loading, setLoading] = useState(true);
  const [fullSlug, setFullSlug] = useState<string>();
  const [publishedAt, setPublishedAt] = useState<string | null>();

  const handleClick = useCallback(() => {
    emitter('', 'insight clicked', {
      'insight clicked name': insight?.title,
    });
  }, [insight, emitter]);

  useEffect(() => {
    fetchStories({
      byUuids: 'id' in blok.insight ? blok.insight.id : undefined,
      page: 1,
      perPage: 1,
    }).then(([story]) => {
      if (!story) {
        return;
      }
      setFullSlug((story as StoryData<FranchiseStoryblok>).full_slug);
      setPublishedAt((story as StoryData<FranchiseStoryblok>).published_at);
      setInsight((story as StoryData<FranchiseStoryblok>).content);
      setLoading(false);
    });
  }, [blok.insight]);

  if (loading || !insight) {
    return <ArticleItemSkeleton variant={variant} />;
  }

  const overline = [];

  if (publishedAt) {
    overline.push(
      formatDate(publishedAt, {
        includeYear: true,
        includeWeekday: false,
      }),
    );
  }

  const readingTime = insight.article
    ? readingTimeCalculator(getWordCount(insight.article))
    : 0;

  if (readingTime > 1) {
    overline.push(t('readingTime', { minutes: readingTime }));
  }

  return (
    <ArticleItem
      variant={variant}
      image={{
        alt: insight.poster.alt || `Image for: ${insight.title}`,
        filename: insight.poster.filename,
        focus: insight.poster.focus,
        ratio: insight.posterRatio || '1/1',
      }}
      href={`/${fullSlug}`}
      onClick={handleClick}
      title={insight.title}
      description={insight.subtitle}
      text={overline.join(' · ')}
    />
  );
};
